import React from 'react'
import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { Box, useTheme } from '@mui/material'

import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { J2Multiply, J2Validate } from '@e3dc-react/icons'
import { UseFetchProps } from '@e3dc-react/shell/Hooks/useFetch'
import { getCityWithZip, getFullName } from 'src/Libs/utils/string-functions'
import { CompanyConnectedRecordDto } from '../api/dto'
import { getPartnerNumberWithSpaces } from '../functions'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'

/**
 * This hook holds all data for the company api view
 */
export const useCompanyTableData = (): {
  fetchProps: UseFetchProps
  columns: ApiTableColumn<CompanyConnectedRecordDto>[]
} => {
  const theme = useTheme()
  const { t } = useCustomTranslation()

  const fetchProps: FetchProps = {
    url: 'users/companies',
    orderBy: 'firma.Name',
    connect: 'manager,location',
  }

  const columns: ApiTableColumn<CompanyConnectedRecordDto>[] = [
    {
      name: 'firma.Name',
      label: t('common.company'),
      value: item => (
        <LinkButton
          hasUnderline={false}
          linkColor={theme.palette.primary.main}
          content={<>{item['firma.Name']}</>}
          link={`/overview/companies/${item['firma.ID']}`}
        />
      ),
    },
    {
      name: 'manager.Name',
      label: t('service-dashboard.company.manager'),
      value: item => getFullName(item['manager.Vorname']!, item['manager.Name']!),
    },
    {
      name: 'manager.Email',
      label: t('service-dashboard.company.manager-email'),
      value: item => item['manager.Email'],
    },
    {
      name: 'location.Telefon',
      label: t('common.phone'),
      value: item => item['location.Telefon'],
    },
    {
      name: 'location.Address',
      label: t('common.address'),
      value: item => {
        return (
          <Box>
            <Box>{item['location.PlaceOR']}</Box>
            <Box>{getCityWithZip(item['location.PLZ']!, item['location.Address']!)}</Box>
          </Box>
        )
      },
    },
    {
      name: 'firma.partnerNumber',
      label: t('common.partner-number'),
      value: item => getPartnerNumberWithSpaces(item['firma.partnerNumber'].toString()),
    },
    {
      name: 'firma.NDA_submit',
      label: t('service-dashboard.company.nda'),
      value: item =>
        item['firma.NDA_submit'] ? (
          <J2Validate
            sx={theme => ({
              color: theme.palette.success.main,
            })}
          />
        ) : (
          <J2Multiply
            sx={theme => ({
              color: theme.palette.error.main,
            })}
          />
        ),
    },
    {
      name: 'firma.shopPermission',
      label: t('service-dashboard.company.shop-permission'),
      value: item =>
        item['firma.shopPermission'] ? (
          <J2Validate
            sx={theme => ({
              color: theme.palette.success.main,
            })}
          />
        ) : (
          <J2Multiply
            sx={theme => ({
              color: theme.palette.error.main,
            })}
          />
        ),
    },
  ]

  return { columns, fetchProps }
}
